import { isValidLatitude, isValidLongitude, MaterialIcon, useMoment } from '@gorila-shared-ui/components';
import { memo, useEffect } from 'react';
import { Marker } from 'react-map-gl/mapbox';
import { useRecoilValue } from 'recoil';
import { useCamera } from '../../../hooks/useCamera';
import { useStyles } from '../../../hooks/useStyles';
import { activeDoorState, reportDoorLngLatsState, reportDoorState } from '../../../storage/reports';
import { Device } from '../../../types/device';
import { StyledTooltip } from '../../ui/StyledTooltip';

function DoorStateMarkers() {
  const { css, theme } = useStyles();
  const assetId = useRecoilValue(activeDoorState);
  const states = useRecoilValue(reportDoorState(assetId || ''));
  const lngLats = useRecoilValue(reportDoorLngLatsState(assetId || ''));
  const { fitToLngLats } = useCamera();
  const { formatDateFromTs } = useMoment();
  const iconWrapper = (isOpen: boolean) =>
    css({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 0,
      height: '25px',
      width: '25px',
      borderRadius: '50%',
      background: isOpen ? theme.colors.negative : theme.colors.backgroundInversePrimary,
      paddingBottom: '1px',
      paddingRight: '1px',
      boxSizing: 'border-box',
    });

  useEffect(() => {
    if (!lngLats.length) return;

    fitToLngLats(lngLats);
  }, [lngLats, assetId, fitToLngLats]);

  if (!states?.length) return null;

  return (
    <>
      {states.map((state, index) => {
        const longitude = state.position_longitude;
        const latitude = state.position_latitude;

        if (!latitude || !longitude) return null;

        if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) return null;

        const isOpen = state.asset.isDoorOpen ?? false;

        const tooltipContent = `${assetId ? '' : (state.asset.device as Device).ident + ': '}${isOpen ? 'Apertura' : 'Cierre'} - ${formatDateFromTs(state.timestamp)}`;

        return (
          <Marker
            longitude={longitude}
            latitude={latitude}
            key={`${index}_${state.asset._id}`}
          >
            <StyledTooltip
              content={tooltipContent}
              ignoreBoundary
              placement="top"
              showArrow={false}
              popoverMargin={15}
            >
              <span className={iconWrapper(isOpen)}>
                <MaterialIcon
                  name="car_door"
                  size={theme.sizing.scale650}
                  color={theme.colors.contentInversePrimary}
                />
              </span>
            </StyledTooltip>
          </Marker>
        );
      })}
    </>
  );
}

export default memo(DoorStateMarkers);
